import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "common/api/httpClient";
import { fetchDeveloperForm } from "store/form/formDataSlice";

export type FormDefinition = {
  id: string;
  name: string;
  description: string;
  disabled: boolean;
  workspaceId: string;
  displayName?: string | null;
  displayDescription?: string | null;
  connect?: boolean | null;
};

type FormsCRUDState = {
  isLoading: boolean;
  error?: string;
  list: FormDefinition[];
  workspaceIdSelected: string;
};

const initialState: FormsCRUDState = {
  isLoading: false,
  list: [],
  workspaceIdSelected: "",
};

export const getFormDefinitions = createAsyncThunk(
  "developer/formsCRUD/getFormDefinitions",
  async () => {
    const response = await httpClient.get<FormDefinition[]>("FormDefinition");
    return response.data;
  }
);

export const createFormDefinition = createAsyncThunk(
  "developer/formsCRUD/createFormDefinition",
  async (formDefinition: FormDefinition) => {
    const response = await httpClient.post<FormDefinition>("FormDefinition", {
      name: formDefinition.name,
      description: formDefinition.description,
      workspaceId: formDefinition.workspaceId,
      connect: formDefinition.connect,
      displayName: formDefinition.displayName,
      displayDescription: formDefinition.displayDescription,
    });
    return response.data;
  }
);

export const deleteFormDefinition = createAsyncThunk(
  "developer/formsCRUD/deleteFormDefinition",
  async (formDefinitionId: string) => {
    await httpClient.delete(`FormDefinition/${formDefinitionId}`, {});
    return formDefinitionId;
  }
);

export const editFormDefinition = createAsyncThunk(
  "developer/formsCRUD/editFormDefinition",
  async (formDefinition: FormDefinition) => {
    await httpClient.put(`FormDefinition/${formDefinition.id}`, formDefinition);
    return formDefinition;
  }
);

export const developerFormsCRUDSlice = createSlice({
  name: "developer/formsCRUD",
  initialState,
  reducers: {
    setWorkspaceIdSelected: (state, action: PayloadAction<string>): void => {
      state.workspaceIdSelected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getFormDefinitions.pending, (state) => {
      state.isLoading = true;
      state.error = "";
    });
    builder.addCase(getFormDefinitions.fulfilled, (state, action) => {
      state.isLoading = false;
      state.list = action.payload;
    });
    builder.addCase(getFormDefinitions.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
    builder.addCase(createFormDefinition.pending, (state) => {
      state.isLoading = true;
      state.error = "";
    });
    builder.addCase(createFormDefinition.fulfilled, (state, action) => {
      state.isLoading = false;
      state.list.push({
        id: action.payload.id,
        name: action.payload.name,
        description: action.payload.description,
        workspaceId: action.payload.workspaceId,
        disabled: action.payload.disabled,
        connect: action.payload.connect,
      });
    });
    builder.addCase(createFormDefinition.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
    builder.addCase(deleteFormDefinition.pending, (state) => {
      state.isLoading = true;
      state.error = "";
    });
    builder.addCase(deleteFormDefinition.fulfilled, (state, action) => {
      state.isLoading = false;
      const formDefinitionId = action.payload;
      if (formDefinitionId) {
        state.list = state.list.filter(
          (formDefinition) => formDefinition.id !== formDefinitionId
        );
      }
    });
    builder.addCase(deleteFormDefinition.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
    builder.addCase(editFormDefinition.pending, (state) => {
      state.isLoading = true;
      state.error = "";
    });
    builder.addCase(editFormDefinition.fulfilled, (state, action) => {
      state.isLoading = false;
      const formDefinition = action.payload;
      const index = state.list.findIndex(
        (listItem) => listItem.id === formDefinition.id
      );
      state.list.splice(index, 1, formDefinition);
    });
    builder.addCase(editFormDefinition.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
    builder.addCase(fetchDeveloperForm.fulfilled, (state, action) => {
      const formDefinition = action.payload.dynamicFormDefinition;

      const findedIndex = state.list.findIndex(
        (form) => form.id === formDefinition.id
      );

      if (findedIndex !== -1) {
        const newFormDefinition: FormDefinition = {
          id: formDefinition.id,
          name: formDefinition.name,
          description: formDefinition.description,
          disabled: formDefinition.disabled,
          workspaceId: formDefinition.workspaceId,
        };
        state.list.splice(findedIndex, 1, newFormDefinition);
      }
    });
  },
});

export const { setWorkspaceIdSelected } = developerFormsCRUDSlice.actions;
